import { useMemo } from "react";
import { Box, Typography, Chip, Link, SelectChangeEvent } from "@mui/material";
import WidgetCard from "@arelia-components/ui-atoms/WidgetCard";
import WidgetMenuSelector from "@arelia-components/ui-atoms/WidgetMenuSelector";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import { PostForMeAccount, PlatformPost, SocialPlatform } from "../../api/postForMe";
import { DataGrid } from "@arelia-components/ui-atoms/data-grid/DataGrid";
import { ColumnDef, ValueColumnDefParams } from "@arelia-components/ui-atoms/data-grid/models";
import { normalizeMetrics } from "./util";
import { EmptyMessage } from "@arelia-components/ui-atoms/EmptyMessage";
import { Inventory2Outlined } from "@mui/icons-material";

interface PostsAnalyticsPresenterProps {
  accounts: PostForMeAccount[] | undefined;
  isLoadingAccounts: boolean;
  selectedAccountId: string;
  onAccountChange: (event: SelectChangeEvent<string>) => void;
  posts: PlatformPost[];
  isLoadingFeed: boolean;
  error?: Error | null;
}

export default function PostsAnalyticsPresenter({
  accounts,
  isLoadingAccounts,
  selectedAccountId,
  onAccountChange,
  posts,
  isLoadingFeed,
  error
}: PostsAnalyticsPresenterProps) {
  const columns = useMemo<ColumnDef[]>(() => [
        {
      field: "platform_url",
      headerName: "Preview",
      align: "center",
      width: "100px",
      renderer: ({ value, row }: ValueColumnDefParams) => {
        let thumbnailUrl = null;
        if (row.media) {
          if (Array.isArray(row.media) && row.media.length) {
            const first = row.media[0];
            thumbnailUrl = typeof first === 'string' ? first : (first.thumbnail_url || first.url);
          } else if (typeof row.media === 'object') {
            thumbnailUrl = row.media.thumbnail_url || row.media.url;
          } else if (typeof row.media === 'string') {
            thumbnailUrl = row.media;
          }
        }

        const content = thumbnailUrl ? (
          <Box 
            component="img" 
            src={thumbnailUrl} 
            alt="Post thumbnail" 
            sx={styles.thumbnailImage} 
          />
        ) : (
          <Box sx={styles.thumbnailPlaceholder}>
            <OpenInNewIcon color="action" />
          </Box>
        );

        return value ? (
          <Link href={value} target="_blank" rel="noopener" sx={styles.centerFlex}>
            {content}
          </Link>
        ) : (
          <Box sx={styles.centerFlex}>
            {content}
          </Box>
        );
      }
    },
    {
      field: "caption",
      headerName: "Caption",
      width: "30%",
      renderer: ({ row }: ValueColumnDefParams) => (
        <Typography noWrap title={row.caption}>
          {row.caption || "(No Caption)"}
        </Typography>
      )
    },
    {
      field: "platform",
      headerName: "Platform",
      align: "right",
      renderer: ({ value }: ValueColumnDefParams) => {
        const platform = String(value || '').toLowerCase();
        let icon;
        let sx: any = { ...styles.platformChip };
        
        if (platform === SocialPlatform.Facebook) {
          icon = <FacebookIcon />;
          sx = { ...sx, ...styles.facebookChip };
        } else if (platform === SocialPlatform.Instagram) {
          icon = <InstagramIcon />;
          sx = { ...sx, ...styles.instagramChip };
        } else if (platform === SocialPlatform.TikTok) {
          icon = <MusicNoteIcon />;
          sx = { ...sx, ...styles.tiktokChip };
        }
        
        return <Chip label={value} size="small" icon={icon} sx={sx} />;
      }
    },
    {
      field: "posted_at",
      headerName: "Date",
      align: "right",
      formatter: ({ value }: ValueColumnDefParams) => value ? new Date(value).toLocaleDateString() : "-"
    },
    {
      field: "metrics",
      headerName: "Views",
      align: "right",
      renderer: ({ value }: ValueColumnDefParams) => normalizeMetrics(value).views.toString()
    },
    {
      field: "metrics",
      headerName: "Likes",
      align: "right",
      renderer: ({ value }: ValueColumnDefParams) => normalizeMetrics(value).likes.toString()
    },
    {
      field: "metrics",
      headerName: "Comments",
      align: "right",
      renderer: ({ value }: ValueColumnDefParams) => normalizeMetrics(value).comments.toString()
    },
    {
      field: "metrics",
      headerName: "Shares",
      align: "right",
      renderer: ({ value }: ValueColumnDefParams) => normalizeMetrics(value).shares.toString()
    }
  ], []);

  const handleAccountSelect = (value: string) => {
    onAccountChange({ target: { value } } as SelectChangeEvent<string>);
  };

  const menuItems = useMemo(() => {
    if (!accounts) return [];

    const getPlatformIcon = (platform: string) => {
      const p = platform.toLowerCase();
      if (p === SocialPlatform.Facebook) return <FacebookIcon color="primary" fontSize="small" sx={styles.iconMargin} />;
      if (p === SocialPlatform.Instagram) return <InstagramIcon color="error" fontSize="small" sx={styles.iconMargin} />;
      if (p === SocialPlatform.TikTok) return <MusicNoteIcon color="secondary" fontSize="small" sx={styles.iconMargin} />;
      return null;
    };

    return [
      { label: "All Accounts", value: "all" },
      ...accounts.map(account => ({
        label: (
          <Box component="span" sx={styles.menuItemLabel}>
            {getPlatformIcon(account.platform)}
            {account.username}
          </Box>
        ),
        value: account.id
      }))
    ];
  }, [accounts]);

  const renderAccountSelector = () => (
    <Box sx={styles.headerSelector}>
      <WidgetMenuSelector<string>
        onClick={handleAccountSelect}
        selectedValue={selectedAccountId}
        menuItems={menuItems}
      />
    </Box>
  );

  return (
    <WidgetCard
      widgetId="POSTS_ANALYTICS"
      title="Posts Analytics"
      headerComponent={!isLoadingAccounts && accounts?.length ? renderAccountSelector() : undefined}
      loading={isLoadingAccounts || isLoadingFeed}
      error={!!error && !isLoadingAccounts && !isLoadingFeed}
    >
      <Box sx={styles.cardContent}>
        {!accounts?.length ? (
          <Box sx={styles.emptyContainer}>
            <EmptyMessage icon={Inventory2Outlined} message={"No connected social accounts found or your subscription does not include My Social feature"} />
          </Box>
        ) : (
          <DataGrid<PlatformPost>
            rows={posts || []}
            columns={columns}
            pageSize={10}
            rowsPerPage={[10, 20, 50]}
          />
        )}
      </Box>
    </WidgetCard>
  );
}

const styles = {
  headerSelector: { 
    display: 'flex', 
    alignItems: 'center', 
    gap: '6px' 
  },
  menuItemLabel: { 
    display: 'flex', 
    alignItems: 'center'
  },
  cardContent: { 
    flex: 1, 
    overflow: 'hidden', 
    height: '100%' 
  },
  thumbnailImage: { 
    width: 56, 
    height: 56, 
    objectFit: 'cover', 
    borderRadius: 2,
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    transition: 'transform 0.2s',
    '&:hover': {
      transform: 'scale(1.05)'
    }
  },
  thumbnailPlaceholder: { 
    width: 56, 
    height: 56, 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center',
    bgcolor: 'action.hover',
    borderRadius: 2
  },
  centerFlex: { 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center' 
  },
  platformChip: { 
    fontWeight: 500 
  },
  facebookChip: { 
    bgcolor: '#1877F2', 
    color: 'white', 
    '& .MuiChip-icon': { color: 'white' } 
  },
  instagramChip: { 
    background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)', 
    color: 'white', 
    '& .MuiChip-icon': { color: 'white' } 
  },
  tiktokChip: { 
    bgcolor: '#000000', 
    color: 'white', 
    '& .MuiChip-icon': { color: 'white' } 
  },
  iconMargin: { 
    mr: 1 
  },
  emptyContainer: { 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center', 
    height: '100%' 
  }
};
